import { ContainmentStatus } from "enums/containmentStatus";
import { ContainmentDTO } from "models/dto/containment";
import * as containmentStatusMapper from "models/mapper/containmentStatus";
import * as sectorMapper from "models/mapper/sectorMapper";
import * as opt from "lib/opt";
import { Sector } from "./Sector";
import * as impactedPlantMapper from "models/mapper/impactedPlant";
import { ImpactedPlant } from "./impactedPlant";
import * as constants from "lib/util/constants";
import { ContainmentTypeConfig, GetContainmentTypeConfig } from "pages/Upload/hooks/containmentTypeConfig";

// Interface for the API request structure
export interface ContainmentModel {
	containment: ContainmentDTO;
}

// Matches the Java BaseData structure

export class Containment extends ContainmentDTO {
	private containmentStatusMapper = containmentStatusMapper.New();
	private sectorMapper = sectorMapper.New();

	get Status(): ContainmentStatus {
		return this.containmentStatusMapper.Map(this.status);
	}

	get Sector(): Sector {
		return (this as any).sector as Sector;
	}

	get Plants(): ImpactedPlant[] {
		return impactedPlantMapper
			.New()
			.MapArray(this.impactedPlants.Map((p) => p.plant));
	}

	TypeNames(): string[] {
		return [...new Set(
			(this.impactedSectorTypes || [])
				.map(ist => ist.type?.name)
				.filter(Boolean)
		)] as string[];
	}

	get TypeConfig(): ContainmentTypeConfig | undefined {
		return GetContainmentTypeConfig(this.SourceSystem);
	}

	get SourceSystem(): string {
		return this.sourceSystem ? this.sourceSystem : (this.TypeNames()[0] ?? "");
	}

	/**
	 * Converts this Containment object to a DTO suitable for API submission
	 * Ensures all required fields are present and unwrapped from Option types
	 */
	ToDTO(): ContainmentDTO {
		return {
			id: this.id,
			name: this.name,
			description: this.description,
			friendlyId: this.friendlyId,
			tcn: this.tcn,
			createDate: this.createDate,
			updateDate: this.updateDate,
			createUser: this.createUser,
			updateUser: this.updateUser,
			totalSerialCount: this.totalSerialCount,
			status: this.status,
			reason: this.reason,

			// Unwrap Option types
			sector: opt.IsOpt((this as any).sector)
				? (this as any).sector.UnwrapOr(null)
				: (this as any).sector,
			sectorType: opt.IsOpt((this as any).sectorType)
				? (this as any).sectorType.UnwrapOr(null)
				: (this as any).sectorType,
			initial: opt.IsOpt(this.initial)
				? this.initial.UnwrapOr(null)
				: this.initial,
			subsequent: opt.IsOpt(this.subsequent)
				? this.subsequent.UnwrapOr(null)
				: this.subsequent,

			// Ensure array properties are initialized
			impactedPlants: this.impactedPlants || [],
		} as ContainmentDTO;
	}

	/**
	 * Creates the API payload structure for updating a containment
	 * @returns The API model structure expected by the server
	 */
	ToApiModel(): ContainmentModel {
		return { containment: this.ToDTO().Copy() };
	}

	/**
	 * Creates a model for API creation (without ID)
	 * @returns API model for creation
	 */
	ToApiCreateModel(): Omit<ContainmentModel, "containment.id"> {
		const model = this.ToApiModel();
		delete (model.containment as any).id;
		return model;
	}
}
